import React, { useEffect, useState, useCallback, ReactNode } from 'react';
import { merchantApi } from '../api/config';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBook,
  faLightbulb,
  faGear,
  faBell,
  faBellSlash,
  faPaperPlane,
  faCheckCircle,
  faSave,
  faLock,
  faInfoCircle,
  faEye,
  faEyeSlash,
  faReceipt,
  faWallet,
  faHashtag,
  faKey,
  faComments,
} from '@fortawesome/free-solid-svg-icons';
import { faTelegram } from '@fortawesome/free-brands-svg-icons';
import { useTranslation } from 'react-i18next';
import { UiPageHeader, UiButton, UiInput, UiToggle, UiAlert, StatusBadge } from '../components/ui';
import type { StatusDef } from '../components/ui';
import TelegramTopicField from '../components/TelegramTopicField';

// สถานะ webhook ของบอท (backend ถาม Telegram สด) — system_bot = ใช้บอทของระบบ ไม่ต้องตั้งอะไร · own_bot = บอทของร้าน ระบบตั้ง webhook ให้ตอนบันทึก
interface TelegramWebhookStatus {
  mode: 'system_bot' | 'own_bot';
  registered: boolean;
  url?: string | null;
  pending_update_count?: number;
  last_error_message?: string | null;
  last_error_at?: string | null;
  error?: string | null;
}

interface TelegramSettings {
  telegram_bot_token?: string | null;
  telegram_chat_id?: string | null;
  telegram_enabled: boolean;
  is_configured: boolean;
  // ห้องแนบสลิปในกลุ่ม Topics — null = รับทุกห้อง · topic_supported:false = tenant ยังไม่รองรับ (ซ่อนช่อง)
  telegram_topic_slip_id?: number | null;
  topic_supported?: boolean;
  webhook?: TelegramWebhookStatus | null;
}

interface TelegramForm {
  telegram_bot_token: string;
  telegram_chat_id: string;
  telegram_enabled: boolean;
  telegram_topic_slip_id: string;
}

const EMPTY_FORM: TelegramForm = {
  telegram_bot_token: '',
  telegram_chat_id: '',
  telegram_enabled: false,
  telegram_topic_slip_id: '',
};

// class ชุดเดียวกับหน้า settings อื่น (WebhookSettingsPage) — การ์ด/หัวการ์ด/แถวสถานะ รองรับ dark mode
const CARD_CLASS = 'bg-white dark:bg-slate-800 rounded-2xl shadow-theme p-6';
const CARD_TITLE_CLASS = 'text-lg font-bold text-gray-800 dark:text-slate-100 flex items-center gap-2 mb-5';
const STATUS_ROW_CLASS = 'flex items-center justify-between gap-3 bg-gray-50 dark:bg-slate-700/50 rounded-xl px-4 py-3';
const STATUS_LABEL_CLASS = 'text-sm text-gray-700 dark:text-slate-200';
const CODE_CLASS = 'bg-gray-100 dark:bg-slate-700 text-gray-800 dark:text-slate-200 px-1 rounded';

const CONFIG_STATUS_MAP: Record<string, StatusDef> = {
  configured: { bg: 'bg-green-100 dark:bg-green-900/30', text: 'text-green-700 dark:text-green-400', icon: faCheckCircle },
  partial:    { bg: 'bg-blue-100 dark:bg-blue-900/30',   text: 'text-blue-700 dark:text-blue-300',   icon: faInfoCircle },
  none:       { bg: 'bg-amber-100 dark:bg-amber-900/30', text: 'text-amber-700 dark:text-amber-300', icon: faInfoCircle },
};

const ENABLED_STATUS_MAP: Record<string, StatusDef> = {
  on:  { bg: 'bg-green-100 dark:bg-green-900/30', text: 'text-green-700 dark:text-green-400', icon: faBell },
  off: { bg: 'bg-gray-200 dark:bg-slate-700',     text: 'text-gray-600 dark:text-slate-300',  icon: faBellSlash },
};

const WEBHOOK_STATUS_MAP: Record<string, StatusDef> = {
  system:  { bg: 'bg-green-100 dark:bg-green-900/30', text: 'text-green-700 dark:text-green-400', icon: faCheckCircle },
  active:  { bg: 'bg-green-100 dark:bg-green-900/30', text: 'text-green-700 dark:text-green-400', icon: faCheckCircle },
  missing: { bg: 'bg-amber-100 dark:bg-amber-900/30', text: 'text-amber-700 dark:text-amber-300', icon: faInfoCircle },
  error:   { bg: 'bg-red-100 dark:bg-red-900/30',     text: 'text-red-700 dark:text-red-400',     icon: faInfoCircle },
};

/** สรุปสถานะ webhook เป็น key ของ badge + ข้อความรายละเอียด (ถ้ามี) */
function summarizeWebhook(w: TelegramWebhookStatus | null | undefined): { key: string; detail?: string } {
  if (!w) return { key: 'missing' };
  if (w.mode === 'system_bot') return { key: 'system' };
  if (w.error) return { key: 'error', detail: w.error };
  if (!w.registered) return { key: 'missing', detail: w.url ? `ชี้ไปที่อื่น: ${w.url}` : undefined };
  if (w.last_error_message) return { key: 'error', detail: `${w.last_error_message}${w.pending_update_count ? ` · ค้าง ${w.pending_update_count} ข้อความ` : ''}` };
  return { key: 'active' };
}

/** ขั้นตอนในคู่มือ: เลขวงกลม + หัวข้อ + รายการย่อย */
const GuideStep: React.FC<{ n: number; title: string; children: ReactNode }> = ({ n, title, children }) => (
  <div className="flex gap-4">
    <div className="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-300 flex items-center justify-center font-bold shrink-0">
      {n}
    </div>
    <div className="min-w-0">
      <h4 className="font-semibold text-gray-800 dark:text-slate-100">{title}</h4>
      <ul className="text-gray-600 dark:text-slate-300 text-sm mt-1 space-y-1 list-disc list-inside break-words">
        {children}
      </ul>
    </div>
  </div>
);

const TelegramSettingsPage: React.FC = () => {
  const { t } = useTranslation();
  const [settings, setSettings] = useState<TelegramSettings>({
    telegram_bot_token: null,
    telegram_chat_id: null,
    telegram_enabled: false,
    is_configured: false,
  });
  const [formData, setFormData] = useState<TelegramForm>(EMPTY_FORM);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);

  const loadSettings = useCallback(async () => {
    try {
      const response = await merchantApi.get('/merchants/telegram/settings');
      const data: TelegramSettings = response.data.data;
      setSettings(data);

      // แสดงข้อมูลที่มีอยู่แล้ว แม้จะตั้งค่าไม่ครบ (เช่น มี token แต่ไม่มี chat_id) — token ไม่ส่งกลับมา ช่องจึงว่างเสมอ
      if (data.is_configured || data.telegram_bot_token || data.telegram_chat_id) {
        setFormData({
          telegram_bot_token: '',
          telegram_chat_id: data.telegram_chat_id || '',
          telegram_enabled: data.telegram_enabled,
          telegram_topic_slip_id: data.telegram_topic_slip_id != null ? String(data.telegram_topic_slip_id) : '',
        });
      }
    } catch (error: any) {
      toast.error(t('telegram.loadError'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const setField = <K extends keyof TelegramForm>(field: K, value: TelegramForm[K]) =>
    setFormData((prev) => ({ ...prev, [field]: value }));

  const handleSave = async () => {
    // ถ้ามี token อยู่แล้วใน DB ไม่ต้องกรอกใหม่ แค่ต้องมี chat_id
    const hasExistingToken = !!settings.telegram_bot_token;
    if (!formData.telegram_bot_token && !hasExistingToken) {
      toast.error(t('telegram.tokenRequired'));
      return;
    }
    // Chat ID เว้นว่างได้: บันทึก token ก่อนให้ระบบตั้ง webhook → ค่อยพิมพ์ /getid ในกลุ่มเอา Chat ID มาใส่

    setSaving(true);
    try {
      // ไม่ได้กรอก token ใหม่ → ส่ง flag บอก backend ว่าใช้ token เดิม
      // ห้องแนบสลิป: ส่ง key เฉพาะเมื่อ tenant รองรับ (ว่าง = null = รับทุกห้อง) — ไม่ส่ง = backend ไม่แตะค่าเดิม
      const { telegram_topic_slip_id, ...rest } = formData;
      const payload: Record<string, unknown> = {
        ...rest,
        telegram_bot_token: formData.telegram_bot_token || undefined,
        keep_existing_token: !formData.telegram_bot_token && hasExistingToken,
      };
      if (settings.topic_supported !== false) {
        payload.telegram_topic_slip_id = telegram_topic_slip_id === '' ? null : Number(telegram_topic_slip_id);
      }

      const { data } = await merchantApi.post('/merchants/telegram/settings', payload);
      // บันทึกได้แต่ตั้ง webhook ให้บอทของร้านไม่สำเร็จ (เช่น token ถูกระบบอื่นใช้ / ระบบไม่มี URL สาธารณะ) → เตือนค้างไว้ให้อ่าน
      if (data?.webhook_warning) {
        toast.warn(data.message, { autoClose: 12000 });
      } else {
        toast.success(t('telegram.saveSuccess'));
      }
      await loadSettings();
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('common.error'));
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    if (!settings.is_configured) {
      toast.error(t('telegram.configureFirst'));
      return;
    }
    setTesting(true);
    try {
      const { data } = await merchantApi.post('/merchants/telegram/test');
      // ตั้งห้องแนบสลิปไว้แต่ส่งเข้าห้องนั้นไม่ได้ (เลขผิด/ห้องถูกลบ) → backend ส่งเข้ากลุ่มหลักแทนและบอกมาเป็น topic_warning
      if (data?.topic_warning) {
        toast.warn(data.message, { autoClose: 10000 });
      } else {
        toast.success(t('telegram.testSuccess'));
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('telegram.testError'));
    } finally {
      setTesting(false);
    }
  };

  const handleToggle = async (enabled: boolean) => {
    if (!settings.is_configured) {
      toast.error(t('telegram.setupFirstToggle'));
      return;
    }
    try {
      await merchantApi.post('/merchants/telegram/toggle', { enabled });
      setSettings((prev) => ({ ...prev, telegram_enabled: enabled }));
      toast.success(enabled ? t('telegram.notificationEnabled') : t('telegram.notificationDisabled'));
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('common.error'));
    }
  };

  const configStatus = settings.is_configured ? 'configured' : settings.telegram_bot_token ? 'partial' : 'none';
  const configLabel = settings.is_configured
    ? t('telegram.configured')
    : settings.telegram_bot_token
      ? t('telegram.hasTokenNoChatId')
      : t('telegram.notConfigured');

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin w-12 h-12 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="w-full">
      <UiPageHeader
        icon={faTelegram}
        title={t('telegram.title')}
        subtitle={t('telegram.desc')}
        actions={
          <button
            type="button"
            onClick={() => setShowInstructions((v) => !v)}
            className="px-4 py-2 rounded-lg text-sm font-medium bg-white/20 hover:bg-white/30 text-white flex items-center gap-2 backdrop-blur-sm border border-white/20"
          >
            <FontAwesomeIcon icon={showInstructions ? faEyeSlash : faEye} />
            {showInstructions ? t('telegram.hideSetup') : t('telegram.showSetup')}
          </button>
        }
      />

      {/* คู่มือตั้งค่า (พับได้) */}
      {showInstructions && (
        <div className={`${CARD_CLASS} mb-6`}>
          <h3 className={CARD_TITLE_CLASS}>
            <FontAwesomeIcon icon={faBook} className="text-blue-500" /> {t('telegram.setupGuide')}
          </h3>
          <div className="space-y-4">
            <GuideStep n={1} title={t('telegram.step1Title')}>
              <li>เปิด Telegram และค้นหา <code className={CODE_CLASS}>@BotFather</code></li>
              <li>ส่งคำสั่ง <code className={CODE_CLASS}>/newbot</code></li>
              <li>ตั้งชื่อ Bot ของคุณ</li>
              <li>คัดลอก <strong>Bot Token</strong> ที่ได้รับ</li>
            </GuideStep>
            <GuideStep n={2} title={t('telegram.step2Title')}>
              <li>กรอก Bot Token ในฟอร์มด้านล่างแล้ว<strong>บันทึก</strong>ก่อน (Chat ID เว้นว่างได้) — ระบบจะตั้งค่าให้ Bot ส่งข้อความมาที่ระบบเอง</li>
              <li>เชิญ Bot เข้ากลุ่มของร้าน แล้ว<strong>ตั้งเป็นผู้ดูแลกลุ่ม (admin)</strong> — ไม่งั้น Bot จะมองไม่เห็นรูปสลิป · กลุ่มเดียวควรมี Bot ของระบบนี้แค่ตัวเดียว</li>
              <li>พิมพ์ <code className={CODE_CLASS}>/getid</code> ในกลุ่ม Bot จะตอบ <strong>Chat ID</strong> (ตัวเลขติดลบ) มาให้ → นำมาใส่ช่อง Chat ID แล้วบันทึกอีกครั้ง</li>
              <li>กลุ่มที่เปิด <strong>Topics</strong>: พิมพ์ <code className={CODE_CLASS}>/getid</code> ในห้องที่ต้องการให้บอทรับสลิป จะได้ <strong>Topic ID</strong> มาใส่ช่อง "ห้องแนบสลิป" (เว้นว่าง = รับทุกห้อง)</li>
              <li>หรือเปิด <code className={`${CODE_CLASS} text-xs`}>https://api.telegram.org/bot&lt;TOKEN&gt;/getUpdates</code> แล้วดู <code className={CODE_CLASS}>chat.id</code></li>
            </GuideStep>
            <GuideStep n={3} title={t('telegram.step3Title')}>
              <li>กรอก Bot Token และ Chat ID ในฟอร์มด้านล่าง</li>
              <li>กดปุ่ม "บันทึก" แล้วกด "ทดสอบ" เพื่อให้ Bot ส่งข้อความเข้ากลุ่ม</li>
            </GuideStep>
          </div>
          <UiAlert tone="blue" icon={faLightbulb} className="mt-4">
            {t('telegram.tokenSafe')}
          </UiAlert>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* ตั้งค่า Bot */}
        <div className={CARD_CLASS}>
          <h2 className={CARD_TITLE_CLASS}>
            <FontAwesomeIcon icon={faGear} style={{ color: 'var(--theme-primary)' }} /> {t('telegram.botSettings')}
          </h2>

          <div className="space-y-4">
            <UiInput
              id="telegram_bot_token"
              label="Bot Token"
              icon={faKey}
              required
              type="text"
              autoComplete="off"
              placeholder={settings.telegram_bot_token ? '••••••••••••••••••' : t('telegram.botTokenPlaceholder')}
              value={formData.telegram_bot_token}
              onChange={(e) => setField('telegram_bot_token', e.target.value)}
              hint={settings.telegram_bot_token ? `${t('telegram.currentToken')} ${settings.telegram_bot_token}` : undefined}
            />
            {settings.telegram_bot_token && (
              <p className="-mt-3 text-xs text-gray-400 dark:text-slate-500 flex items-center gap-1">
                <FontAwesomeIcon icon={faLock} /> {t('telegram.tokenKeepHint')}
              </p>
            )}
            <p className="-mt-2 text-xs text-gray-500 dark:text-slate-400">{t('telegram.ownBotNote')}</p>

            <UiInput
              id="telegram_chat_id"
              label="Chat ID"
              icon={faComments}
              type="text"
              inputMode="numeric"
              autoComplete="off"
              placeholder={t('telegram.chatIdPlaceholder')}
              value={formData.telegram_chat_id}
              onChange={(e) => setField('telegram_chat_id', e.target.value)}
              hint={t('telegram.chatIdHint')}
            />

            <TelegramTopicField
              value={formData.telegram_topic_slip_id}
              onChange={(v) => setField('telegram_topic_slip_id', v)}
              supported={settings.topic_supported !== false}
              label={t('telegram.topicLabel')}
              hint={t('telegram.topicHint')}
              placeholder={t('telegram.topicPlaceholder')}
            />

            <div className="flex flex-col sm:flex-row gap-3 pt-2">
              <UiButton icon={faSave} onClick={handleSave} loading={saving} className="justify-center sm:flex-1">
                {saving ? t('telegram.savingSettings') : t('telegram.saveSettings')}
              </UiButton>
              {settings.is_configured && (
                <UiButton variant="secondary" icon={faPaperPlane} onClick={handleTest} loading={testing} className="justify-center">
                  {testing ? t('telegram.testSending') : t('telegram.test')}
                </UiButton>
              )}
            </div>
          </div>
        </div>

        {/* สถานะ */}
        <div className={CARD_CLASS}>
          <h2 className={CARD_TITLE_CLASS}>
            <FontAwesomeIcon icon={faBell} className="text-amber-500" /> {t('telegram.notificationStatus')}
          </h2>

          <div className="space-y-3">
            <div className={STATUS_ROW_CLASS}>
              <span className={STATUS_LABEL_CLASS}>{t('telegram.configStatus')}</span>
              <StatusBadge status={configStatus} statusMap={CONFIG_STATUS_MAP}>{configLabel}</StatusBadge>
            </div>

            <div className={STATUS_ROW_CLASS}>
              <span className={STATUS_LABEL_CLASS}>{t('telegram.notification')}</span>
              <StatusBadge status={settings.telegram_enabled ? 'on' : 'off'} statusMap={ENABLED_STATUS_MAP}>
                {settings.telegram_enabled ? t('telegram.enabled') : t('telegram.disabled')}
              </StatusBadge>
            </div>

            {settings.telegram_bot_token && (() => {
              const w = summarizeWebhook(settings.webhook);
              const labelKey = { system: 'telegram.webhookSystemBot', active: 'telegram.webhookActive', missing: 'telegram.webhookMissing', error: 'telegram.webhookError' }[w.key] || 'telegram.webhookMissing';
              return (
                <div className={`${STATUS_ROW_CLASS} flex-wrap`}>
                  <span className={STATUS_LABEL_CLASS}>{t('telegram.webhookStatus')}</span>
                  <StatusBadge status={w.key} statusMap={WEBHOOK_STATUS_MAP}>{t(labelKey)}</StatusBadge>
                  {w.detail && <p className="basis-full text-xs text-gray-500 dark:text-slate-400 break-all">{w.detail}</p>}
                </div>
              );
            })()}

            {settings.is_configured && (
              <div className={STATUS_ROW_CLASS}>
                <span className={`${STATUS_LABEL_CLASS} font-medium`}>
                  {settings.telegram_enabled ? t('telegram.enableNotification') : t('telegram.disableNotification')}
                </span>
                <UiToggle
                  checked={settings.telegram_enabled}
                  onChange={handleToggle}
                  ariaLabel={t('telegram.notification')}
                />
              </div>
            )}
          </div>

          <UiAlert tone="blue" icon={faTelegram} className="mt-5">
            <p className="font-bold mb-2">{t('telegram.botDoes')}</p>
            <ul className="space-y-1.5">
              <li className="flex items-start gap-2">
                <FontAwesomeIcon icon={faReceipt} className="mt-0.5 shrink-0" />
                <span>{t('telegram.featureSlip')}</span>
              </li>
              <li className="flex items-start gap-2">
                <FontAwesomeIcon icon={faWallet} className="mt-0.5 shrink-0" />
                <span>{t('telegram.featureWallet')}</span>
              </li>
              <li className="flex items-start gap-2">
                <FontAwesomeIcon icon={faHashtag} className="mt-0.5 shrink-0" />
                <span>{t('telegram.featureGetId')}</span>
              </li>
            </ul>
          </UiAlert>

          {settings.telegram_enabled && (
            <UiAlert tone="green" className="mt-4">
              <strong>{t('telegram.ready')}</strong> {t('telegram.readyDesc')}
            </UiAlert>
          )}
        </div>
      </div>
    </div>
  );
};

export default TelegramSettingsPage;
